import { useSearchParams } from 'react-router-dom';
import { Dispatch, ReactElement, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { Filter, Product, ProductClass } from '../types/catalog';
import { fetchCategoryClasses, fetchCategoryFilters, fetchProducts } from '../util/api';
import { CategoriesContext } from '../App';

export const pageSizes = [10, 20, 35, 50];

export enum SortOrder {
    Asc = 'asc',
    Desc = 'desc',
}

export const sortValues = {
    article: 'Артикул',
    name: 'Имя',
};
export type SortKey = keyof typeof sortValues;

export interface FilterValues {
    [id: string]: string[];
}

const prepareFilterValues = (filterValues: FilterValues) =>
    Object.entries(filterValues)
        .filter(([_, values]) => values.length)
        .map(([id, values]) => ({
            id,
            values,
        }));

export interface CatalogRenderProps {
    query: string;
    sortBy: SortKey;
    sortOrder: SortOrder;
    page: number;
    pageSize: number;
    classes: ProductClass[];
    classId: string | null;
    filters: Filter[];
    filterValues: FilterValues;
    products: Product[];
    productsStart: number;
    productsEnd: number;
    productsTotal: number;
    onSortChange: (key: SortKey) => void;
    setPageSize: Dispatch<SetStateAction<number>>;
    setPage: Dispatch<SetStateAction<number>>;
    setQuery: Dispatch<SetStateAction<string>>;
    setClassId: (id: string | null) => void;
    toggleFilterValue: (fIdx: number, vIdx: number) => void;
    resetFilters: () => void;
}

interface CatalogProps {
    globalMode?: boolean;
    render: (props: CatalogRenderProps) => ReactElement | null;
}

function Catalog({ globalMode = false, render }: CatalogProps) {
    const { categorySlugs } = useContext(CategoriesContext);
    const [params, setParams] = useSearchParams();
    const [classes, setClasses] = useState([] as ProductClass[]);
    const [filters, setFilters] = useState([] as Filter[]);
    const [products, setProducts] = useState([] as Product[]);
    const [productsTotal, setProductsTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    // классы и фильтры еще не прогружены, нужно не дать их сбросить
    const [firstRun, setFirstRun] = useState(true);
    const [filterValues, setFilterValues] = useState(() => {
        try {
            return JSON.parse(params.get('filters') || '{}') as FilterValues;
        } catch (e) {
            return {};
        }
    });
    const [sortBy, setSortBy] = useState(() => (params.get('sortBy') || 'article') as SortKey);
    const [sortOrder, setSortOrder] = useState(() => (params.get('sortOrder') || SortOrder.Asc) as SortOrder);
    const [query, setQuery] = useState(() => params.get('q') || '');
    const [classId, setClassId] = useState(() => params.get('classId'));
    const [pageSize, setPageSize] = useState(() => parseInt(params.get('pageSize') || '0') || pageSizes[0]);
    const [page, setPage] = useState(() => parseInt(params.get('page') || '1'));
    // тянем классы
    useEffect(() => {
        fetchCategoryClasses(categorySlugs, query).then((classes) => {
            setClasses(classes);
        });
    }, [categorySlugs, query]);
    // сбрасываем класс, если выбранный id не существует
    useEffect(() => {
        if (firstRun) {
            return;
        }
        if (classId && !classes.find((c) => c.id === classId)) {
            const newVal = classes.length === 1 ? classes[0].id : null;
            if (newVal !== classId) {
                setLoading(true);
                setClassId(newVal);
                setPage(1);
            }
        }
    }, [firstRun, classes, classId]);
    // тянем фильтры
    useEffect(() => {
        setLoading(true);
        fetchCategoryFilters(categorySlugs, classId, query, prepareFilterValues(filterValues)).then(
            (filters) => {
                filters.sort((a, b) => {
                    const nameA = a.name.toLowerCase();
                    const nameB = b.name.toLowerCase();
                    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
                });
                setFilters(filters);
                setFirstRun(false);
            }
        );
    }, [filterValues, categorySlugs, classId, query]);
    // сбрасываем фильтры, если выбранные не существуют
    useEffect(() => {
        if (firstRun) {
            return;
        }
        const newFilterValues = { ...filterValues };
        let hasChanges = false;
        for (const [id, values] of Object.entries(filterValues)) {
            const filter = filters.find((f) => f.id === id);
            if (!filter) {
                delete newFilterValues[id];
                hasChanges = true;
            } else {
                for (const value of values) {
                    if (!filter.values.find((v) => v.value === value)) {
                        delete newFilterValues[id];
                        hasChanges = true;
                        break;
                    }
                }
            }
        }
        if (hasChanges) {
            setLoading(true);
            setFilterValues(newFilterValues);
        } else {
            setLoading(false);
        }
    }, [firstRun, filters, filterValues]);
    // тянем товары
    useEffect(() => {
        if (loading) {
            return;
        }
        const usedFilters = prepareFilterValues(filterValues);
        const filters = {
            query,
            sortBy,
            sortOrder,
            classId,
            filters: usedFilters,
            pageSize,
            page,
        };
        // пишем состояние фильтров в GET параметры
        const params = new URLSearchParams();
        const urlParams = { ...filters, query: null, q: query, filters: filterValues };
        for (const [name, value] of Object.entries(urlParams)) {
            if (value === null) {
                continue;
            }
            let stringValue;
            if (typeof value === 'object') {
                stringValue = JSON.stringify(value);
            } else {
                stringValue = value.toString();
            }
            params.set(name, stringValue);
        }
        setParams(params, { replace: true });
        fetchProducts(categorySlugs, filters).then(({ total, products }) => {
            setProducts(products);
            setProductsTotal(total);
        });
    }, [loading, setParams, categorySlugs, query, sortBy, sortOrder, classId, filterValues, pageSize, page]);

    const toggleFilterValue = useCallback(
        (fIdx, vIdx) => {
            const { id, values } = filters[fIdx];
            const value = values[vIdx].value;
            setFilterValues((values) => {
                const newValues = { ...values };
                if (!newValues[id]) {
                    newValues[id] = [value];
                } else {
                    const set = new Set(newValues[id]);
                    if (set.has(value)) {
                        set.delete(value);
                    } else {
                        set.add(value);
                    }
                    if (set.size) {
                        newValues[id] = Array.from(set);
                    } else {
                        delete newValues[id];
                    }
                }
                setPage(1);
                return newValues;
            });
        },
        [filters]
    );

    const resetFilters = useCallback(() => {
        if (!globalMode) {
            setQuery('');
        }
        setFilterValues({});
    }, [globalMode]);
    const _setClassId = useCallback((classId: string | null) => {
        setLoading(true);
        setPage(1);
        setClassId(classId);
    }, []);
    const onSortChange = useCallback(
        (key: SortKey) => {
            if (sortBy === key) {
                setSortOrder((sortOrder) => (sortOrder === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc));
            } else {
                setSortBy(key);
                setSortOrder(SortOrder.Asc);
            }
        },
        [sortBy]
    );
    const max = (val: number) => (val > productsTotal ? productsTotal : val);
    const productsStart = max(pageSize * (page - 1) + 1);
    const productsEnd = max(pageSize * page);
    const Component = render;
    return (
        <Component
            query={query}
            sortBy={sortBy}
            sortOrder={sortOrder}
            page={page}
            pageSize={pageSize}
            classes={classes}
            classId={classId}
            filters={filters}
            filterValues={filterValues}
            products={products}
            productsStart={productsStart}
            productsEnd={productsEnd}
            productsTotal={productsTotal}
            onSortChange={onSortChange}
            setPageSize={setPageSize}
            setPage={setPage}
            setQuery={setQuery}
            setClassId={_setClassId}
            toggleFilterValue={toggleFilterValue}
            resetFilters={resetFilters}
        />
    );
}

export default Catalog;
